(() => {
  const root = document.documentElement;
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const trapFocus = (container) => {
    const focusable = qsa(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      container
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    first.focus();
    return () => container.removeEventListener('keydown', handler);
  };

  const toggleRegion = (region, open, trigger) => {
    if (!region) return;
    region.hidden = !open;
    trigger?.setAttribute('aria-expanded', String(open));
    root.classList.toggle('is-locked', open);
  };

  const mobileMenu = qs('[data-mobile-menu]');
  const mobileToggle = qs('[data-menu-toggle]');
  let releaseMenuFocus = () => {};
  mobileToggle?.addEventListener('click', () => {
    const open = mobileMenu.hidden;
    toggleRegion(mobileMenu, open, mobileToggle);
    if (open) releaseMenuFocus = trapFocus(mobileMenu);
    else releaseMenuFocus();
  });
  qsa('[data-menu-close]').forEach((button) =>
    button.addEventListener('click', () => {
      releaseMenuFocus();
      toggleRegion(mobileMenu, false, mobileToggle);
      mobileToggle?.focus();
    })
  );

  const searchDialog = qs('[data-search-dialog]');
  const searchButton = qs('[data-search-open]');
  let releaseSearchFocus = () => {};
  const closeSearch = () => {
    releaseSearchFocus();
    toggleRegion(searchDialog, false, searchButton);
    searchButton?.focus();
  };
  searchButton?.addEventListener('click', () => {
    toggleRegion(searchDialog, true, searchButton);
    releaseSearchFocus = trapFocus(searchDialog);
    qs('input[type="search"]', searchDialog)?.focus();
  });
  qsa('[data-search-close]').forEach((button) => button.addEventListener('click', closeSearch));

  const drawer = qs('[data-cart-drawer]');
  const drawerContent = qs('[data-cart-drawer-content]');
  let releaseCartFocus = () => {};
  let lastCartTrigger;

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pl-PL', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const getPromoTiers = () => {
    const promoBar = qs('[data-promo-tiers]');
    if (!promoBar?.dataset.promoTiers) return [];
    return promoBar.dataset.promoTiers
      .split(',')
      .map((tier) => {
        const [minimum, discount] = tier.split(':').map(Number);
        return { minimum: minimum * 100, discount: discount * 100 };
      })
      .filter((tier) => Number.isFinite(tier.minimum) && Number.isFinite(tier.discount))
      .sort((first, second) => first.minimum - second.minimum);
  };

  const promoProgressMarkup = (cart) => {
    const tiers = getPromoTiers();
    if (!tiers.length || !cart.item_count) return '';

    const subtotal = cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price;
    const unlocked = [...tiers].reverse().find((tier) => subtotal >= tier.minimum);
    const next = tiers.find((tier) => subtotal < tier.minimum);
    let message;
    let progress = 100;

    if (!next) {
      message = `Najwyższy próg odblokowany: ${formatMoney(tiers.at(-1).discount, cart.currency)} rabatu.`;
    } else {
      const previousMinimum = unlocked?.minimum ?? 0;
      const interval = Math.max(next.minimum - previousMinimum, 1);
      progress = Math.min(100, Math.max(0, ((subtotal - previousMinimum) / interval) * 100));
      const remaining = next.minimum - subtotal;
      message = unlocked
        ? `Próg ${formatMoney(unlocked.discount, cart.currency)} odblokowany. Dodaj produkty za ${formatMoney(remaining, cart.currency)}, aby otrzymać ${formatMoney(next.discount, cart.currency)} rabatu.`
        : `Dodaj produkty za ${formatMoney(remaining, cart.currency)}, aby otrzymać ${formatMoney(next.discount, cart.currency)} rabatu.`;
    }

    return `<div class="promo-progress">
      <p class="promo-progress__label">Promocja progowa</p>
      <p class="promo-progress__message">${escapeHtml(message)}</p>
      <div class="promo-progress__track" aria-hidden="true">
        <span style="width: ${progress.toFixed(2)}%"></span>
      </div>
      <p class="promo-progress__note">Rabat nalicza się automatycznie. Promocje nie łączą się.</p>
    </div>`;
  };

  const updatePromoProgress = (cart) => {
    qsa('[data-promo-progress]').forEach((container) => {
      container.innerHTML = promoProgressMarkup(cart);
    });
  };

  const cartMarkup = (cart) => {
    if (!cart.item_count) {
      return `<div class="empty-state"><p>${escapeHtml(window.themeStrings.empty)}</p>
        <a class="button button--outline" href="/collections/all">${escapeHtml(window.themeStrings.continue || 'Kontynuuj zakupy')}</a></div>`;
    }
    const items = cart.items.map((item, index) => {
      const image = item.featured_image?.url
        ? `<img src="${escapeHtml(item.featured_image.url)}&width=240" alt="${escapeHtml(item.featured_image.alt || item.product_title)}" width="120" height="150">`
        : '<div class="cart-line__placeholder" aria-hidden="true"></div>';
      const options = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="meta">${escapeHtml(item.variant_title)}</p>`
        : '';
      return `<article class="cart-line">
        <a class="cart-line__media" href="${escapeHtml(item.url)}">${image}</a>
        <div class="cart-line__details">
          <a class="cart-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${options}
          <p>${formatMoney(item.final_line_price, cart.currency)}</p>
          <div class="cart-line__actions">
            <label><span class="visually-hidden">${escapeHtml(window.themeStrings.quantity || 'Ilość')}</span>
              <input type="number" min="0" value="${item.quantity}" data-cart-quantity data-line="${index + 1}">
            </label>
            <button class="text-button" type="button" data-cart-remove data-line="${index + 1}">${escapeHtml(window.themeStrings.remove)}</button>
          </div>
        </div>
      </article>`;
    }).join('');
    return `<div class="cart-drawer__items">${items}</div>
      <div class="cart-drawer__footer">
        ${promoProgressMarkup(cart)}
        <div class="subtotal"><span>${escapeHtml(window.themeStrings.subtotal)}</span><strong>${formatMoney(cart.total_price, cart.currency)}</strong></div>
        <a class="button button--outline" href="${window.routes.cartPage}">${escapeHtml(window.themeStrings.viewCart || 'Zobacz koszyk')}</a>
        <form action="/cart" method="post"><button class="button" type="submit" name="checkout">${escapeHtml(window.themeStrings.checkout)}</button></form>
      </div>`;
  };

  const bindCartControls = () => {
    qsa('[data-cart-quantity]', drawerContent).forEach((input) => {
      input.addEventListener('change', () => changeCart(input.dataset.line, Number(input.value)));
    });
    qsa('[data-cart-remove]', drawerContent).forEach((button) => {
      button.addEventListener('click', () => changeCart(button.dataset.line, 0));
    });
  };

  const renderCart = async (open = true) => {
    const response = await fetch(window.routes.cart, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    const cart = await response.json();
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    updatePromoProgress(cart);
    if (drawerContent) {
      drawerContent.innerHTML = cartMarkup(cart);
      bindCartControls();
    }
    if (open && drawer) {
      drawer.hidden = false;
      root.classList.add('is-locked');
      releaseCartFocus = trapFocus(drawer);
    }
    return cart;
  };

  const closeCart = () => {
    releaseCartFocus();
    if (drawer) drawer.hidden = true;
    root.classList.remove('is-locked');
    lastCartTrigger?.focus();
  };

  const changeCart = async (line, quantity) => {
    try {
      const response = await fetch(window.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity })
      });
      if (!response.ok) throw new Error('Cart change failed');
      await renderCart(true);
    } catch {
      drawerContent?.setAttribute('data-error', window.themeStrings.error);
    }
  };

  qsa('[data-cart-open]').forEach((button) => {
    button.addEventListener('click', () => {
      lastCartTrigger = button;
      renderCart(true).catch(() => {
        window.location.href = window.routes.cartPage;
      });
    });
  });
  qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));

  qsa('form[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!window.fetch || !drawer) return;
      event.preventDefault();
      const submit = qs('[type="submit"]', form);
      submit?.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(window.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Add failed');
        lastCartTrigger = submit;
        await renderCart(true);
      } catch {
        form.submit();
      } finally {
        submit?.removeAttribute('aria-busy');
      }
    });
  });

  qsa('[data-product-form-main]').forEach((form) => {
    const optionSelects = qsa('[data-product-option]', form);
    const variantInput = qs('[data-variant-id]', form);
    const variantsNode = form.parentElement?.querySelector('[data-product-variants]');
    const priceNode = form.parentElement?.querySelector('[data-product-price]');
    const skuNode = qs('[data-product-sku]', form);
    const availabilityNode = qs('[data-product-availability]', form);
    const submit = qs('[type="submit"]', form);
    if (!variantInput || !variantsNode || !submit) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch {
      return;
    }

    const selectedOptions = () => optionSelects.map((select) => select.value);

    const matchingVariant = () => {
      const values = selectedOptions();
      return variants.find((variant) =>
        variant.options?.every((value, index) => value === values[index])
      );
    };

    const refreshOptionAvailability = () => {
      if (!optionSelects.length) return;
      const currentValues = selectedOptions();
      optionSelects.forEach((select) => {
        const position = Number(select.dataset.optionPosition);
        [...select.options].forEach((option) => {
          const possible = variants.some((variant) =>
            variant.options?.every((value, index) => {
              if (index === position) return value === option.value;
              return value === currentValues[index];
            })
          );
          option.disabled = !possible && option.value !== select.value;
        });
      });
    };

    const showMedia = (variant) => {
      const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      if (!mediaId) return;
      const gallery = qs('[data-product-gallery]');
      qsa('[data-gallery-media]', gallery).forEach((media) => {
        media.hidden = media.dataset.mediaId !== String(mediaId);
      });
      qsa('[data-gallery-thumb]', gallery).forEach((thumb) => {
        thumb.setAttribute(
          'aria-current',
          String(thumb.dataset.mediaId === String(mediaId))
        );
      });
    };

    const renderVariant = () => {
      const variant = matchingVariant();
      if (!variant) {
        variantInput.value = '';
        submit.disabled = true;
        submit.textContent = window.themeStrings.unavailable;
        if (skuNode) skuNode.textContent = '—';
        if (availabilityNode) {
          availabilityNode.textContent = window.themeStrings.unavailable;
        }
        return;
      }
      variantInput.value = variant.id;
      if (priceNode) {
        const compareAt = variant.compare_at_price > variant.price
          ? ` <s>${formatMoney(variant.compare_at_price, window.shopCurrency)}</s>`
          : '';
        priceNode.innerHTML = `<span class="price${compareAt ? ' price--sale' : ''}">${formatMoney(variant.price, window.shopCurrency)}${compareAt}</span>`;
      }
      submit.disabled = !variant.available;
      submit.textContent = variant.available
        ? window.themeStrings.addToCart
        : window.themeStrings.soldOut;
      if (skuNode) skuNode.textContent = variant.sku || '—';
      if (availabilityNode) {
        availabilityNode.textContent = variant.available
          ? window.themeStrings.available
          : window.themeStrings.soldOut;
      }
      showMedia(variant);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch {
        // Variant selection still works if the preview URL cannot be rewritten.
      }
    };

    optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        refreshOptionAvailability();
        renderVariant();
      });
    });
    refreshOptionAvailability();
    renderVariant();
  });

  qsa('[data-gallery-thumb]').forEach((button) => {
    button.addEventListener('click', () => {
      const gallery = button.closest('[data-product-gallery]');
      qsa('[data-gallery-media]', gallery).forEach((media) => {
        media.hidden = media.dataset.mediaId !== button.dataset.mediaId;
      });
      qsa('[data-gallery-thumb]', gallery).forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb === button));
      });
    });
  });

  qsa('[data-sort-by]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });

  if (qs('[data-promo-progress]')) {
    renderCart(false).catch(() => {});
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (drawer && !drawer.hidden) closeCart();
    if (searchDialog && !searchDialog.hidden) closeSearch();
    if (mobileMenu && !mobileMenu.hidden) {
      releaseMenuFocus();
      toggleRegion(mobileMenu, false, mobileToggle);
      mobileToggle?.focus();
    }
  });
})();
